import { poly, type GenericPolyElement, type PointBezierHandle } from '..';
import { reversionElement } from '../../../lib/element';
import { getPlugin } from '../../../lib/plugin';
import { distance, pointRotateRads, type Point } from '../../../utils/math';

import { initHandlesFromPoints, offsetHandles } from './bezierHandles';
import { getBoundsWithBezier } from './bounds';
import { offsetPoints } from './transform';

export const translatePoint = (
  pointIndex: number,
  element: GenericPolyElement,
  originalElement: GenericPolyElement,
  [pointerX, pointerY]: Point,
  [originX, originY]: Point,
  isBeforeHandle?: boolean,
  isAfterHandle?: boolean,
) => {
  let updatedPoints = [...originalElement.points];
  let updatedBezierHandles = [
    ...(originalElement.bezierHandles ?? initHandlesFromPoints(updatedPoints)),
  ];

  const dx = pointerX - originX;
  const dy = pointerY - originY;

  //transform current point
  if (!isBeforeHandle && !isAfterHandle) {
    const [transformedPointX, transformedPointY] =
      originalElement.points[pointIndex];

    updatedPoints[pointIndex] = [
      transformedPointX + dx,
      transformedPointY + dy,
    ];

    updatedBezierHandles[pointIndex] = {
      before: [
        updatedBezierHandles[pointIndex].before[0] + dx,
        updatedBezierHandles[pointIndex].before[1] + dy,
      ],
      after: [
        updatedBezierHandles[pointIndex].after[0] + dx,
        updatedBezierHandles[pointIndex].after[1] + dy,
      ],
    };
  } else {
    updatedBezierHandles[pointIndex] = {
      before: isBeforeHandle
        ? [
            updatedBezierHandles[pointIndex].before[0] + dx,
            updatedBezierHandles[pointIndex].before[1] + dy,
          ]
        : updatedBezierHandles[pointIndex].before,
      after: isAfterHandle
        ? [
            updatedBezierHandles[pointIndex].after[0] + dx,
            updatedBezierHandles[pointIndex].after[1] + dy,
          ]
        : updatedBezierHandles[pointIndex].after,
    };
  }

  const [smallestX, smallestY] = getBoundsWithBezier(
    updatedPoints,
    updatedBezierHandles,
  );

  updatedPoints = offsetPoints(updatedPoints, -smallestX, -smallestY);

  updatedBezierHandles = offsetHandles(
    updatedBezierHandles,
    -smallestX,
    -smallestY,
  )!;

  const [minX, minY, maxX, maxY] = getBoundsWithBezier(
    updatedPoints,
    updatedBezierHandles,
  );
  const newWidth = distance(minX, maxX);
  const newHeight = distance(minY, maxY);

  const newX = originalElement.x + smallestX;
  const newY = originalElement.y + smallestY;

  const updated: GenericPolyElement = {
    ...originalElement,
    height: newHeight,
    points: updatedPoints,
    bezierHandles: updatedBezierHandles,
    width: newWidth,
    x: newX,
    y: newY,
    ...reversionElement(element, ['bounds', 'canvas']),
  };

  updated.shape = getPlugin(updated.type).collision.calculateShape(updated);

  return updated;
};

export const rotatePoly = (
  originalElement: GenericPolyElement,
  angle: number,
) => {
  const [, , , , cx, cy] = getBoundsWithBezier(
    originalElement.points,
    originalElement.bezierHandles,
  );

  const center: Point = [cx, cy];

  let updatedPoints = originalElement.points.map<Point>((point) => {
    const [rotatedX, rotatedY] = pointRotateRads(point, center, angle);
    return [rotatedX, rotatedY];
  });

  let updatedHandles = originalElement.bezierHandles?.map<PointBezierHandle>(
    (handle) => ({
      before: pointRotateRads(handle.before, center, angle),
      after: pointRotateRads(handle.after, center, angle),
    }),
  );

  const [minX, minY, maxX, maxY] = getBoundsWithBezier(
    updatedPoints,
    updatedHandles,
  );
  updatedPoints = offsetPoints(updatedPoints, -minX, -minY);
  updatedHandles = offsetHandles(updatedHandles, -minX, -minY);

  const newWidth = distance(minX, maxX);
  const newHeight = distance(minY, maxY);

  const updated: Partial<GenericPolyElement> = {
    height: newHeight,
    points: updatedPoints,
    bezierHandles: updatedHandles,
    width: newWidth,
    ...reversionElement(originalElement, ['bounds', 'canvas']),
  };

  const shape = poly.collision.calculateShape({
    ...originalElement,
    ...updated,
  } as GenericPolyElement);

  updated.shape = shape;

  return updated;
};

export const translatePoints = (
  pointIndices: number[],
  element: GenericPolyElement,
  originalElement: GenericPolyElement,
  [pointerX, pointerY]: Point,
  [originX, originY]: Point,
) => {
  const updatedPoints = [...originalElement.points];
  const updatedBezierHandles = [
    ...(originalElement.bezierHandles ?? initHandlesFromPoints(updatedPoints)),
  ];

  const dx = pointerX - originX;
  const dy = pointerY - originY;

  for (const index of pointIndices) {
    updatedPoints[index] = [
      originalElement.points[index][0] + dx,
      originalElement.points[index][1] + dy,
    ];
    updatedBezierHandles[index] = {
      before: [
        updatedBezierHandles[index].before[0] + dx,
        updatedBezierHandles[index].before[1] + dy,
      ],
      after: [
        updatedBezierHandles[index].after[0] + dx,
        updatedBezierHandles[index].after[1] + dy,
      ],
    };
  }

  const [smallestX, smallestY] = getBoundsWithBezier(
    updatedPoints,
    updatedBezierHandles,
  );

  const normalizedPoints = offsetPoints(updatedPoints, -smallestX, -smallestY);
  const normalizedHandles = offsetHandles(
    updatedBezierHandles,
    -smallestX,
    -smallestY,
  )!;

  const [minX, minY, maxX, maxY] = getBoundsWithBezier(
    normalizedPoints,
    normalizedHandles,
  );

  const updated: GenericPolyElement = {
    ...originalElement,
    height: distance(minY, maxY),
    points: normalizedPoints,
    bezierHandles: normalizedHandles,
    width: distance(minX, maxX),
    x: originalElement.x + smallestX,
    y: originalElement.y + smallestY,
    ...reversionElement(element, ['bounds', 'canvas']),
  };

  updated.shape = getPlugin(updated.type).collision.calculateShape(updated);

  return updated;
};
